/**
 * Mock data for local test mode (REACT_APP_A11Y_TEST=true).
 * Provides realistic RoleGroupedPrehireResult and UserRolesResponse
 * so all role tabs (including Work on Behalf) can be tested without a backend.
 */

import {
    AppRole,
    PrehireDto,
    RoleGroupedPrehireResult,
    UserRolesResponse,
    WorkOnBehalfEntry,
} from '../../generated/api.onboarding.client';

const managerPrehires: PrehireDto[] = [
    {
        candidate: {
            firstName: 'Alice',
            lastName: 'Chen',
            fullName: 'Alice Chen',
            email: 'alice.chen@example.com',
            jobTitle: 'Software Engineer II',
            startDate: new Date('2026-05-10'),
            requisitionNumber: 'REQ-100201',
            workLocationCity: 'Redmond',
            geoRequisitionCountry: 'US',
        },
        hiringManager: { name: 'Test Manager', email: 'testmanager@microsoft.com', firstName: 'Test', lastName: 'Manager' },
        crmCaseId: 'HR-2026-0001',
        statuses: {
            BackgroundCheck: { rollupStatus: 'Completed', status: 'Cleared', isRequired: true },
            Immigration: { rollupStatus: 'NotApplicable', status: 'N/A', isRequired: false },
            I9: { rollupStatus: 'InProgress', status: 'Documents received', isRequired: true },
            EmploymentAgreement: { rollupStatus: 'Completed', status: 'Signed', isRequired: true },
            EmployeeProfile: { rollupStatus: 'InProgress', status: 'Pending', isRequired: true },
            StartDate: { rollupStatus: 'Completed', status: 'Confirmed', isRequired: true },
            MicrosoftAccount: { rollupStatus: 'NotStarted', status: 'To be defined', isRequired: true },
        },
    },
    {
        candidate: {
            firstName: 'Brian',
            lastName: 'Patel',
            fullName: 'Brian Patel',
            email: 'brian.patel@example.com',
            jobTitle: 'Senior Product Manager',
            startDate: new Date('2026-04-28'),
            requisitionNumber: 'REQ-100202',
            workLocationCity: 'Seattle',
            geoRequisitionCountry: 'US',
        },
        hiringManager: { name: 'Test Manager', email: 'testmanager@microsoft.com', firstName: 'Test', lastName: 'Manager' },
        crmCaseId: 'HR-2026-0002',
        statuses: {
            BackgroundCheck: { rollupStatus: 'Completed', status: 'Cleared', isRequired: true },
            I9: { rollupStatus: 'Completed', status: 'Verified', isRequired: true },
            EmploymentAgreement: { rollupStatus: 'Completed', status: 'Signed', isRequired: true },
            EmployeeProfile: { rollupStatus: 'Completed', status: 'Created', isRequired: true },
            StartDate: { rollupStatus: 'Completed', status: 'Confirmed', isRequired: true },
            MicrosoftAccount: { rollupStatus: 'Completed', status: 'Provisioned', isRequired: true },
        },
    },
];

const wobEntries: WorkOnBehalfEntry[] = [
    {
        hiringManagerEmail: 'sarah.kim@microsoft.com',
        prehires: [
            {
                candidate: {
                    firstName: 'Carlos',
                    lastName: 'Rivera',
                    fullName: 'Carlos Rivera',
                    email: 'carlos.rivera@example.com',
                    jobTitle: 'Cloud Solutions Architect',
                    startDate: new Date('2026-05-20'),
                    requisitionNumber: 'REQ-100301',
                    workLocationCity: 'Austin',
                    geoRequisitionCountry: 'US',
                },
                hiringManager: { name: 'Sarah Kim', email: 'sarah.kim@microsoft.com', firstName: 'Sarah', lastName: 'Kim' },
                crmCaseId: 'HR-2026-0010',
                statuses: {
                    BackgroundCheck: { rollupStatus: 'InProgress', status: 'In review', isRequired: true },
                    Immigration: { rollupStatus: 'InProgress', status: 'Visa sponsorship', isRequired: true },
                    I9: { rollupStatus: 'NotStarted', status: 'To be defined', isRequired: true },
                    EmploymentAgreement: { rollupStatus: 'Completed', status: 'Signed', isRequired: true },
                    EmployeeProfile: { rollupStatus: 'NotStarted', status: 'To be defined', isRequired: true },
                    StartDate: { rollupStatus: 'Completed', status: 'Confirmed', isRequired: true },
                    MicrosoftAccount: { rollupStatus: 'NotStarted', status: 'To be defined', isRequired: true },
                },
            },
            {
                candidate: {
                    firstName: 'Dana',
                    lastName: 'Okafor',
                    fullName: 'Dana Okafor',
                    email: 'dana.okafor@example.com',
                    jobTitle: 'Data Scientist',
                    startDate: new Date('2026-06-01'),
                    requisitionNumber: 'REQ-100302',
                    workLocationCity: 'Redmond',
                    geoRequisitionCountry: 'US',
                },
                hiringManager: { name: 'Sarah Kim', email: 'sarah.kim@microsoft.com', firstName: 'Sarah', lastName: 'Kim' },
                crmCaseId: 'HR-2026-0011',
                statuses: {
                    BackgroundCheck: { rollupStatus: 'Completed', status: 'Cleared', isRequired: true },
                    I9: { rollupStatus: 'Completed', status: 'Verified', isRequired: true },
                    EmploymentAgreement: { rollupStatus: 'Completed', status: 'Signed', isRequired: true },
                    EmployeeProfile: { rollupStatus: 'Completed', status: 'Created', isRequired: true },
                    StartDate: { rollupStatus: 'Completed', status: 'Confirmed', isRequired: true },
                    MicrosoftAccount: { rollupStatus: 'InProgress', status: 'Provisioning', isRequired: true },
                },
            },
        ],
    },
    {
        hiringManagerEmail: 'james.wong@microsoft.com',
        prehires: [
            {
                candidate: {
                    firstName: 'Elise',
                    lastName: 'Martin',
                    fullName: 'Elise Martin',
                    email: 'elise.martin@example.com',
                    jobTitle: 'UX Designer',
                    startDate: new Date('2026-05-05'),
                    requisitionNumber: 'REQ-100401',
                    workLocationCity: 'San Francisco',
                    geoRequisitionCountry: 'US',
                },
                hiringManager: { name: 'James Wong', email: 'james.wong@microsoft.com', firstName: 'James', lastName: 'Wong' },
                crmCaseId: 'HR-2026-0020',
                statuses: {
                    BackgroundCheck: { rollupStatus: 'Expired', status: 'Expired - needs renewal', isRequired: true },
                    I9: { rollupStatus: 'NotStarted', status: 'To be defined', isRequired: true },
                    EmploymentAgreement: { rollupStatus: 'InProgress', status: 'Pending signature', isRequired: true },
                    EmployeeProfile: { rollupStatus: 'NotStarted', status: 'To be defined', isRequired: true },
                    StartDate: { rollupStatus: 'Completed', status: 'Confirmed', isRequired: true },
                    MicrosoftAccount: { rollupStatus: 'NotStarted', status: 'To be defined', isRequired: true },
                },
            },
        ],
    },
    {
        hiringManagerEmail: 'jordan.yerzy@microsoft.com',
        prehires: [
            {
                candidate: {
                    firstName: 'Harper',
                    lastName: 'Cole',
                    fullName: 'Harper Cole',
                    email: 'harper.cole@example.com',
                    jobTitle: 'Program Manager',
                    startDate: new Date('2026-05-12'),
                    requisitionNumber: 'REQ-100501',
                    workLocationCity: 'Redmond',
                    geoRequisitionCountry: 'US',
                },
                hiringManager: { name: 'Jordan Yerzy', email: 'jordan.yerzy@microsoft.com', firstName: 'Jordan', lastName: 'Yerzy' },
                crmCaseId: 'HR-2026-0030',
                statuses: {
                    BackgroundCheck: { rollupStatus: 'Completed', status: 'Cleared', isRequired: true },
                    I9: { rollupStatus: 'InProgress', status: 'Pending review', isRequired: true },
                    EmploymentAgreement: { rollupStatus: 'Completed', status: 'Signed', isRequired: true },
                    EmployeeProfile: { rollupStatus: 'InProgress', status: 'Pending', isRequired: true },
                    StartDate: { rollupStatus: 'Completed', status: 'Confirmed', isRequired: true },
                    MicrosoftAccount: { rollupStatus: 'NotStarted', status: 'To be defined', isRequired: true },
                },
            },
        ],
    },
    {
        hiringManagerEmail: 'dusan.matic@microsoft.com',
        prehires: [
            {
                candidate: {
                    firstName: 'Ivy',
                    lastName: 'Nguyen',
                    fullName: 'Ivy Nguyen',
                    email: 'ivy.nguyen@example.com',
                    jobTitle: 'Product Designer',
                    startDate: new Date('2026-05-15'),
                    requisitionNumber: 'REQ-100502',
                    workLocationCity: 'Vancouver',
                    geoRequisitionCountry: 'CA',
                },
                hiringManager: { name: 'Dusan Matic', email: 'dusan.matic@microsoft.com', firstName: 'Dusan', lastName: 'Matic' },
                crmCaseId: 'HR-2026-0031',
                statuses: {
                    BackgroundCheck: { rollupStatus: 'InProgress', status: 'In review', isRequired: true },
                    I9: { rollupStatus: 'NotStarted', status: 'To be defined', isRequired: true },
                    EmploymentAgreement: { rollupStatus: 'Completed', status: 'Signed', isRequired: true },
                    EmployeeProfile: { rollupStatus: 'NotStarted', status: 'To be defined', isRequired: true },
                    StartDate: { rollupStatus: 'Completed', status: 'Confirmed', isRequired: true },
                    MicrosoftAccount: { rollupStatus: 'NotStarted', status: 'To be defined', isRequired: true },
                },
            },
        ],
    },
    {
        hiringManagerEmail: 'marina.jackson@microsoft.com',
        prehires: [
            {
                candidate: {
                    firstName: 'Noah',
                    lastName: 'Turner',
                    fullName: 'Noah Turner',
                    email: 'noah.turner@example.com',
                    jobTitle: 'Senior Data Engineer',
                    startDate: new Date('2026-05-18'),
                    requisitionNumber: 'REQ-100503',
                    workLocationCity: 'Atlanta',
                    geoRequisitionCountry: 'US',
                },
                hiringManager: { name: 'Marina Jackson', email: 'marina.jackson@microsoft.com', firstName: 'Marina', lastName: 'Jackson' },
                crmCaseId: 'HR-2026-0032',
                statuses: {
                    BackgroundCheck: { rollupStatus: 'Completed', status: 'Cleared', isRequired: true },
                    I9: { rollupStatus: 'Completed', status: 'Verified', isRequired: true },
                    EmploymentAgreement: { rollupStatus: 'Completed', status: 'Signed', isRequired: true },
                    EmployeeProfile: { rollupStatus: 'InProgress', status: 'Pending', isRequired: true },
                    StartDate: { rollupStatus: 'Completed', status: 'Confirmed', isRequired: true },
                    MicrosoftAccount: { rollupStatus: 'InProgress', status: 'Provisioning', isRequired: true },
                },
            },
        ],
    },
    {
        hiringManagerEmail: 'adam.scott@microsoft.com',
        prehires: [
            {
                candidate: {
                    firstName: 'Priya',
                    lastName: 'Shah',
                    fullName: 'Priya Shah',
                    email: 'priya.shah@example.com',
                    jobTitle: 'Site Reliability Engineer',
                    startDate: new Date('2026-05-21'),
                    requisitionNumber: 'REQ-100504',
                    workLocationCity: 'Dublin',
                    geoRequisitionCountry: 'IE',
                },
                hiringManager: { name: 'Adam Scott', email: 'adam.scott@microsoft.com', firstName: 'Adam', lastName: 'Scott' },
                crmCaseId: 'HR-2026-0033',
                statuses: {
                    BackgroundCheck: { rollupStatus: 'InProgress', status: 'In review', isRequired: true },
                    I9: { rollupStatus: 'NotStarted', status: 'To be defined', isRequired: true },
                    EmploymentAgreement: { rollupStatus: 'InProgress', status: 'Pending signature', isRequired: true },
                    EmployeeProfile: { rollupStatus: 'NotStarted', status: 'To be defined', isRequired: true },
                    StartDate: { rollupStatus: 'Completed', status: 'Confirmed', isRequired: true },
                    MicrosoftAccount: { rollupStatus: 'NotStarted', status: 'To be defined', isRequired: true },
                },
            },
        ],
    },
    {
        hiringManagerEmail: 'amanda.kimmel@microsoft.com',
        prehires: [
            {
                candidate: {
                    firstName: 'Lucas',
                    lastName: 'Wells',
                    fullName: 'Lucas Wells',
                    email: 'lucas.wells@example.com',
                    jobTitle: 'Business Program Manager',
                    startDate: new Date('2026-05-25'),
                    requisitionNumber: 'REQ-100505',
                    workLocationCity: 'Chicago',
                    geoRequisitionCountry: 'US',
                },
                hiringManager: { name: 'Amanda Kimmel', email: 'amanda.kimmel@microsoft.com', firstName: 'Amanda', lastName: 'Kimmel' },
                crmCaseId: 'HR-2026-0034',
                statuses: {
                    BackgroundCheck: { rollupStatus: 'Completed', status: 'Cleared', isRequired: true },
                    I9: { rollupStatus: 'InProgress', status: 'Pending review', isRequired: true },
                    EmploymentAgreement: { rollupStatus: 'Completed', status: 'Signed', isRequired: true },
                    EmployeeProfile: { rollupStatus: 'Completed', status: 'Created', isRequired: true },
                    StartDate: { rollupStatus: 'Completed', status: 'Confirmed', isRequired: true },
                    MicrosoftAccount: { rollupStatus: 'InProgress', status: 'Provisioning', isRequired: true },
                },
            },
        ],
    },
    {
        hiringManagerEmail: 'ari.aster@microsoft.com',
        prehires: [
            {
                candidate: {
                    firstName: 'Mila',
                    lastName: 'Santos',
                    fullName: 'Mila Santos',
                    email: 'mila.santos@example.com',
                    jobTitle: 'Marketing Manager',
                    startDate: new Date('2026-05-28'),
                    requisitionNumber: 'REQ-100506',
                    workLocationCity: 'London',
                    geoRequisitionCountry: 'GB',
                },
                hiringManager: { name: 'Ari Aster', email: 'ari.aster@microsoft.com', firstName: 'Ari', lastName: 'Aster' },
                crmCaseId: 'HR-2026-0035',
                statuses: {
                    BackgroundCheck: { rollupStatus: 'Completed', status: 'Cleared', isRequired: true },
                    I9: { rollupStatus: 'Completed', status: 'Verified', isRequired: true },
                    EmploymentAgreement: { rollupStatus: 'Completed', status: 'Signed', isRequired: true },
                    EmployeeProfile: { rollupStatus: 'InProgress', status: 'Pending', isRequired: true },
                    StartDate: { rollupStatus: 'Completed', status: 'Confirmed', isRequired: true },
                    MicrosoftAccount: { rollupStatus: 'NotStarted', status: 'To be defined', isRequired: true },
                },
            },
        ],
    },
];

/**
 * Returns a mock RoleGroupedPrehireResult filtered by role, matching the real API behavior.
 */
export const getMockGroupedPrehireResult = (role?: string, targetHiringManagerEmail?: string): RoleGroupedPrehireResult => {
    if (role === AppRole.WorkOnBehalf) {
        if (targetHiringManagerEmail) {
            return {
                workOnBehalf: wobEntries.filter(entry => entry.hiringManagerEmail?.toLowerCase() === targetHiringManagerEmail.toLowerCase()),
            };
        }
        return { workOnBehalf: wobEntries };
    }
    if (role === AppRole.Manager) {
        return { manager: managerPrehires };
    }
    // Default: return all
    return {
        manager: managerPrehires,
        workOnBehalf: wobEntries,
    };
};

/**
 * Returns a mock UserRolesResponse with Manager + WorkOnBehalf roles.
 */
export const getMockUserRolesResponse = (): UserRolesResponse => ({
    email: 'testmanager@microsoft.com',
    roles: [
        { role: AppRole.Manager },
        {
            role: AppRole.WorkOnBehalf,
            hiringManagerEmails: wobEntries
                .map(entry => entry.hiringManagerEmail)
                .filter((email): email is string => !!email),
        },
    ],
});
